import axios from 'axios';
import { getAuth } from 'firebase/auth';

import config from '../config';

const { BASE_URL } = config;

/**
 * `POST /api/v2/analyze-stack` — what the research says about a supplement
 * list the member typed in, against one health goal (STACK-543, PR 345).
 *
 * Async by design on a cache miss: it answers **202 with a `job_id`**, and the
 * caller polls `/api/job_status` through `StackGenerationContext` so the
 * live-progress screen has something to show.
 *
 * On a **completed cache hit** the async decorator answers **200 with the
 * finished body** (no `job_id`) — the same shape `CreateStack` already handles
 * for generation. Callers must accept both; treating a 200 as failure drops
 * merged / not-in-library flags and the muted notes that depend on them.
 *
 * The endpoint is members-only (`PRO_API_ENDPOINTS` + `@pro_feature_required`)
 * and rate limited per user. A lapsed member reaching it directly gets a 403,
 * which surfaces as the flow's generic failure line rather than as a paywall —
 * gating copy is not this ticket's, and the UI entry point already routes a
 * lapsed member to reactivation instead.
 *
 * @param {object} body from `analysisRequestBody` — the allowlisted shape the
 *   endpoint's own sanitizer will keep. Anything else it strips before the
 *   request is written to Supabase `async_job_cache.input_params`.
 * @returns {Promise<{status: number, data: object}>}
 */
export async function requestStackAnalysis(body) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : '';

  const response = await axios.post(`${BASE_URL}/v2/analyze-stack`, body, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  return {
    status: response?.status ?? 0,
    data: response?.data || {},
  };
}

export default requestStackAnalysis;
